import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, TrendingDown, BarChart3, Award, Zap, Target, ChevronDown, ChevronUp, Brain } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const fmtMoney = (n) => "$" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtMoneyDec = (n) => "$" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => (n >= 0 ? "+" : "") + (n || 0).toFixed(2) + "%";

function HoldingRow({ h, expanded, onToggle }) {
  const isWin = h.pnl_pct >= 0;
  const recommendationColor = {
    strong_buy: "#22c55e", buy: "#22c55e",
    hold: "#f59e0b",
    sell: "#ef4444", strong_sell: "#ef4444",
  }[h.recommendation] || "#888";

  return (
    <>
      <tr style={{ borderTop: "1px solid #1a1d29", cursor: "pointer" }} onClick={() => onToggle(h.ticker)}>
        <td style={{ padding: 10, color: "#fff", fontWeight: 700 }}>
          {expanded ? <ChevronDown size={12} style={{display:"inline",marginRight:4}}/> : <ChevronUp size={12} style={{display:"inline",marginRight:4,transform:"rotate(180deg)"}}/>}
          {h.ticker}
        </td>
        <td style={{ padding: 10, color: "#aaa", fontSize: 11 }}>{h.name?.substring(0, 22)}</td>
        <td style={{ padding: 10, color: "#888", fontSize: 10 }}>{h.sector?.substring(0, 14)}</td>
        <td style={{ padding: 10, color: "#aaa", textAlign: "right" }}>${h.avg_cost?.toFixed(2)}</td>
        <td style={{ padding: 10, color: "#fff", textAlign: "right", fontWeight: 600 }}>${h.current?.toFixed(2)}</td>
        <td style={{ padding: 10, textAlign: "right", color: isWin ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
          {fmtPct(h.pnl_pct)}
        </td>
        <td style={{ padding: 10, textAlign: "right", color: isWin ? "#22c55e" : "#ef4444" }}>
          {h.pnl >= 0 ? "+" : ""}{fmtMoneyDec(h.pnl)}
        </td>
        <td style={{ padding: 10, textAlign: "right", color: "#888", fontSize: 11 }}>{fmtMoneyDec(h.value)}</td>
        <td style={{ padding: 10, textAlign: "right" }}>
          <span style={{
            fontSize: 11, padding: "2px 8px", borderRadius: 12,
            background: h.health_score >= 70 ? "#22c55e33" : h.health_score >= 50 ? "#f59e0b33" : "#ef444433",
            color: h.health_score >= 70 ? "#22c55e" : h.health_score >= 50 ? "#f59e0b" : "#ef4444",
            fontWeight: 600,
          }}>{h.health_score || 0}/100</span>
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: "#0a0c14" }}>
          <td colSpan="9" style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              {/* Fundamentals */}
              <div style={{ background: "#13161f", padding: 12, borderRadius: 8, border: "1px solid #2a2d39" }}>
                <div style={{ fontSize: 11, color: "#a855f7", fontWeight: 600, marginBottom: 8 }}>📊 FUNDAMENTALS</div>
                <Stat label="Market Cap" value={`${h.market_cap_b?.toFixed(1) || 0}B`} />
                <Stat label="P/E (TTM)" value={h.pe_ratio?.toFixed(1) || "—"} />
                <Stat label="Forward P/E" value={h.forward_pe?.toFixed(1) || "—"} />
                <Stat label="PEG Ratio" value={h.peg_ratio?.toFixed(2) || "—"} highlight={h.peg_ratio < 1 && h.peg_ratio > 0 ? "good" : null} />
                <Stat label="Dividend Yield" value={`${h.dividend_yield_pct?.toFixed(2)}%`} />
                <Stat label="Beta" value={h.beta?.toFixed(2) || "—"} />
              </div>

              {/* Quality metrics */}
              <div style={{ background: "#13161f", padding: 12, borderRadius: 8, border: "1px solid #2a2d39" }}>
                <div style={{ fontSize: 11, color: "#22c55e", fontWeight: 600, marginBottom: 8 }}>💎 QUALITY</div>
                <Stat label="ROE" value={`${h.roe_pct}%`} highlight={h.roe_pct > 20 ? "good" : null} />
                <Stat label="Operating Margin" value={`${h.op_margin_pct}%`} highlight={h.op_margin_pct > 25 ? "good" : null} />
                <Stat label="Debt/Equity" value={h.debt_to_equity?.toFixed(2)} highlight={h.debt_to_equity < 0.5 ? "good" : h.debt_to_equity > 2 ? "bad" : null} />
                <Stat label="Rev Growth (YoY)" value={fmtPct(h.rev_growth_y)} highlight={h.rev_growth_y > 15 ? "good" : null} />
                <Stat label="Rev Growth (Quarterly)" value={fmtPct(h.rev_growth_q)} />
                <Stat label="Rev CAGR (5y)" value={fmtPct(h.rev_cagr_5y_pct)} />
              </div>

              {/* Forward projections */}
              <div style={{ background: "#13161f", padding: 12, borderRadius: 8, border: "1px solid #2a2d39" }}>
                <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, marginBottom: 8 }}>🔮 PROJECTIONS</div>
                <Stat label="Revenue Now" value={`$${h.history_5y?.[h.history_5y.length-1]?.revenue_b?.toFixed(2) || 0}B`} />
                <Stat label="Projected 1y" value={`$${h.projected_rev_1y_b?.toFixed(2)}B`} highlight="good" />
                <Stat label="Projected 3y" value={`$${h.projected_rev_3y_b?.toFixed(2)}B`} highlight="good" />
                <Stat label="Projected 5y" value={`$${h.projected_rev_5y_b?.toFixed(2)}B`} highlight="good" />
                {h.analyst_count > 0 && (
                  <>
                    <Stat label={`Analyst Target (${h.analyst_count})`} value={`$${h.analyst_target_mean?.toFixed(2)}`} />
                    <Stat label="Analyst Upside" value={fmtPct(h.analyst_upside_pct)} highlight={h.analyst_upside_pct > 15 ? "good" : null} />
                    <Stat label="Recommendation" value={(h.recommendation || "—").toUpperCase()} highlight={h.recommendation?.includes("buy") ? "good" : h.recommendation?.includes("sell") ? "bad" : null} />
                  </>
                )}
              </div>

              {/* 5-year history mini chart */}
              {h.history_5y && h.history_5y.length > 0 && (
                <div style={{ background: "#13161f", padding: 12, borderRadius: 8, border: "1px solid #2a2d39", gridColumn: "span 2" }}>
                  <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600, marginBottom: 8 }}>📈 5-YEAR REVENUE HISTORY</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: 4, height: 80 }}>
                    {h.history_5y.map((y, i) => {
                      const maxRev = Math.max(...h.history_5y.map(x => x.revenue_b || 0));
                      const heightPct = maxRev > 0 ? (y.revenue_b / maxRev * 100) : 0;
                      return (
                        <div key={i} style={{ flex: 1, textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: "#fff", marginBottom: 2 }}>${y.revenue_b?.toFixed(1)}B</div>
                          <div style={{ height: `${heightPct}%`, background: "linear-gradient(180deg, #3b82f6, #1e40af)", borderRadius: 3, minHeight: 4 }}/>
                          <div style={{ fontSize: 8, color: "#666", marginTop: 2 }}>{y.year}</div>
                          <div style={{ fontSize: 8, color: y.net_margin_pct > 15 ? "#22c55e" : "#888" }}>NM {y.net_margin_pct?.toFixed(0)}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Stat({ label, value, highlight }) {
  const color = highlight === "good" ? "#22c55e" : highlight === "bad" ? "#ef4444" : "#fff";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
      <span style={{ color: "#888" }}>{label}</span>
      <span style={{ color, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function PortfolioCard({ p, color, icon: Icon, expandedTicker, setExpandedTicker }) {
  const isWin = p.pnl_pct >= 0;

  return (
    <div style={{ background: "#0f1218", border: `1px solid ${color}55`, borderRadius: 16, padding: 20, marginBottom: 20 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Icon size={20} style={{ color }} />
            <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{p.name}</div>
          </div>
          <div style={{ fontSize: 11, color: "#888" }}>{p.description}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: isWin ? "#22c55e" : "#ef4444", display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
            {isWin ? <TrendingUp size={18}/> : <TrendingDown size={18}/>} {fmtPct(p.pnl_pct)}
          </div>
          <div style={{ fontSize: 12, color: isWin ? "#22c55e" : "#ef4444" }}>
            {p.pnl >= 0 ? "+" : ""}{fmtMoneyDec(p.pnl)}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Mini label="NAV" value={fmtMoney(p.nav)} primary />
        <Mini label="Invested" value={fmtMoney(p.initial_capital)} />
        <Mini label="Holdings" value={`${p.n_holdings}`} />
        <Mini label="Cash" value={fmtMoney(p.cash)} />
        <Mini label="vs SPY" value={fmtPct(p.alpha_vs_spy)} good={p.alpha_vs_spy > 0} bad={p.alpha_vs_spy < 0} />
        {p.alpha_vs_qqq !== undefined && <Mini label="vs QQQ" value={fmtPct(p.alpha_vs_qqq)} good={p.alpha_vs_qqq > 0} bad={p.alpha_vs_qqq < 0} />}
        <Mini label="Trades" value={p.n_trades} />
      </div>

      {/* Holdings table */}
      <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "#fff", borderBottom: "1px solid #2a2d39" }}>
          📊 Holdings ({p.holdings?.length || 0}) — click row to expand
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#0a0c14", color: "#666", textTransform: "uppercase", fontSize: 10 }}>
                <th style={{ padding: 10, textAlign: "left" }}>Ticker</th>
                <th style={{ padding: 10, textAlign: "left" }}>Name</th>
                <th style={{ padding: 10, textAlign: "left" }}>Sector</th>
                <th style={{ padding: 10, textAlign: "right" }}>Entry</th>
                <th style={{ padding: 10, textAlign: "right" }}>Now</th>
                <th style={{ padding: 10, textAlign: "right" }}>P&L %</th>
                <th style={{ padding: 10, textAlign: "right" }}>P&L $</th>
                <th style={{ padding: 10, textAlign: "right" }}>Value</th>
                <th style={{ padding: 10, textAlign: "right" }}>Health</th>
              </tr>
            </thead>
            <tbody>
              {(p.holdings || []).map((h) => (
                <HoldingRow key={h.ticker} h={h} expanded={expandedTicker === `${p.name}-${h.ticker}`}
                  onToggle={(t) => setExpandedTicker(expandedTicker === `${p.name}-${t}` ? null : `${p.name}-${t}`)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, primary, good, bad }) {
  const color = good ? "#22c55e" : bad ? "#ef4444" : primary ? "#fff" : "#aaa";
  return (
    <div>
      <div style={{ fontSize: 10, color: "#888" }}>{label}</div>
      <div style={{ fontSize: primary ? 18 : 14, fontWeight: primary ? 700 : 600, color }}>{value}</div>
    </div>
  );
}

function BacktestSummary({ bt }) {
  if (!bt) return null;
  const compounders = bt.compounders;
  const beasts = bt.beasts;
  const spy20 = bt.spy_20k;

  return (
    <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, padding: 16, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <BarChart3 size={18} style={{ color: "#a855f7" }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Backtest Results — {bt.period}</div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: "#0a0c14", color: "#666" }}>
            <th style={{ padding: 8, textAlign: "left" }}>Strategy</th>
            <th style={{ padding: 8, textAlign: "right" }}>Initial</th>
            <th style={{ padding: 8, textAlign: "right" }}>Final</th>
            <th style={{ padding: 8, textAlign: "right" }}>Total Return</th>
            <th style={{ padding: 8, textAlign: "right" }}>CAGR</th>
            <th style={{ padding: 8, textAlign: "right" }}>Max DD</th>
            <th style={{ padding: 8, textAlign: "right" }}>Sharpe</th>
            <th style={{ padding: 8, textAlign: "right" }}>Win %</th>
          </tr>
        </thead>
        <tbody>
          {[compounders, beasts, spy20].filter(x => x).map((r, i) => (
            <tr key={i} style={{ borderTop: "1px solid #1a1d29" }}>
              <td style={{ padding: 8, color: "#fff", fontWeight: 600 }}>{r.name}</td>
              <td style={{ padding: 8, color: "#aaa", textAlign: "right" }}>{fmtMoney(r.initial_capital)}</td>
              <td style={{ padding: 8, color: "#fff", textAlign: "right", fontWeight: 600 }}>{fmtMoney(r.final_equity)}</td>
              <td style={{ padding: 8, color: r.total_return_pct > 0 ? "#22c55e" : "#ef4444", textAlign: "right", fontWeight: 700 }}>{fmtPct(r.total_return_pct)}</td>
              <td style={{ padding: 8, color: "#a855f7", textAlign: "right" }}>{fmtPct(r.cagr_pct)}</td>
              <td style={{ padding: 8, color: "#ef4444", textAlign: "right" }}>-{r.max_dd_pct}%</td>
              <td style={{ padding: 8, color: "#3b82f6", textAlign: "right", fontWeight: 600 }}>{r.sharpe?.toFixed(2)}</td>
              <td style={{ padding: 8, color: "#888", textAlign: "right" }}>{r.win_rate_pct ? `${r.win_rate_pct}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PortfoliosTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTicker, setExpandedTicker] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) {
      setError("Not logged in"); setLoading(false); return;
    }
    try {
      const r = await fetch(`${API}/api/skynet-brain/portfolios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) { setError("Session expired"); setLoading(false); return; }
      const j = await r.json();
      if (j.error) setError(j.error);
      else { setData(j); setError(null); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 60000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (error && !data) return <div style={{ padding: 40, color: "#ef4444", textAlign: "center" }}>{error}</div>;
  if (loading && !data) return <div style={{ padding: 40, color: "#888", textAlign: "center" }}>Loading portfolios… (~5s)</div>;
  if (!data) return null;

  const c = data.combined || {};

  return (
    <div style={{ padding: "16px 20px", color: "#eee", maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f1218, #1a1d29)", border: "1px solid #2a2d39", borderRadius: 16, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <Brain size={24} style={{ color: "#3b82f6" }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>Stock Portfolios Lab</div>
          <span style={{ fontSize: 11, padding: "3px 8px", background: "#3b82f633", color: "#3b82f6", borderRadius: 4, fontWeight: 600 }}>2 STRATEGIES</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>COMBINED CAPITAL</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#fff" }}>{fmtMoney(c.total_capital)}</div>
            <div style={{ fontSize: 10, color: "#666" }}>Compounders + Beasts</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>TOTAL NAV</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.total_pnl >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMoney(c.total_nav)}</div>
            <div style={{ fontSize: 11, color: c.total_pnl >= 0 ? "#22c55e" : "#ef4444" }}>{fmtPct(c.total_pnl_pct)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>TOTAL P&L</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.total_pnl >= 0 ? "#22c55e" : "#ef4444" }}>
              {c.total_pnl >= 0 ? "+" : ""}{fmtMoneyDec(c.total_pnl)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>STRATEGIES</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginTop: 4 }}>
              <span style={{ color: "#22c55e" }}>● Compounders</span> $20k<br/>
              <span style={{ color: "#f59e0b" }}>● Growth Beasts</span> $10k
            </div>
          </div>
        </div>
      </div>

      {/* Backtest summary */}
      <BacktestSummary bt={data.backtest} />

      {/* Compounders */}
      {data.compounders && (
        <PortfolioCard p={data.compounders} color="#22c55e" icon={Award} expandedTicker={expandedTicker} setExpandedTicker={setExpandedTicker} />
      )}

      {/* Growth Beasts */}
      {data.beasts && (
        <PortfolioCard p={data.beasts} color="#f59e0b" icon={Zap} expandedTicker={expandedTicker} setExpandedTicker={setExpandedTicker} />
      )}

      <div style={{ marginTop: 20, padding: 12, fontSize: 10, color: "#555", textAlign: "center" }}>
        Auto-refresh 60s • Weekly rebalance Sunday 12:00 UTC • Click rows for fundamentals + projections
      </div>
    </div>
  );
}
